import React, { useState } from 'react';
import styles from './CampaignMetricsPage.module.scss';
import Button from '../common/design-system/Button';
import { Badge } from '../common/design-system/Badge';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../common/design-system/Card';
import { Eye, Users, Heart, MessageCircle, Bookmark, Calendar, MapPin, TrendingUp } from 'lucide-react';

const Stat = ({ icon, label, value }) => (
    <div className={styles.statCard}>
        <div className={styles.statLabel}>
            <span>{label}</span>
            {icon}
        </div>
        <div className={styles.statValue}>{value}</div>
    </div>
);

const CampaignMetricsPage = () => {
    const [activeTab, setActiveTab] = useState('metrics');

    const campaign = {
        name: "BBQ Challenge",
        category: "Restaurants & Night Life",
        status: "ACTIVE",
        country: "Panamá",
        from: "30 Jun 2025",
        to: "8 Jul 2025",
        valuePerCreator: 100,
        slots: 3,
        totalInvestment: 300,
        requirements: "Seguidores: No especificado",
    };

    const totals = {
        impressions: "758.9K",
        reach: "310.8K",
        likes: "16.5K",
        comments: "1.5K",
        saves: "4.3K",
    };

    const creators = [
        { id: 'c1', handle: '@Jude', posts: 1, er: 4.2, status: 'Completada', metrics: { imp: '122.1K' } },
        { id: 'c2', handle: '@Kurt', posts: 1, er: 6.1, status: 'Completada', metrics: { imp: '159.0K' } },
        { id: 'c3', handle: '@palmratech', posts: 1, er: 3.4, status: 'En progreso', metrics: { imp: '92.4K' } },
    ];

    return (
        <div className={styles.page}>
            {/* Top Bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarContent}>
                    <div className={styles.logoArea}>
                        <div className={styles.brand}>FOTH</div>
                        <div className={styles.breadcrumb}>/ Campañas / {campaign.name}</div>
                    </div>
                    <div className={styles.actionsArea}>
                        <Button variant="ghost" size="sm">Dashboard</Button>
                        <Button variant="ghost" size="sm">Mis Collabs</Button>
                        <Button size="sm">Nueva Collab</Button>
                    </div>
                </div>
            </div>

            <div className={styles.mainContainer}>
                {/* Header */}
                <div className={styles.campaignHeader}>
                    <div>
                        <div className={styles.headerTitle}>
                            <h1>{campaign.name}</h1>
                            <Badge variant="outline">{campaign.status}</Badge>
                        </div>
                        <div className={styles.headerMeta}>
                            <span><TrendingUp size={14} />{campaign.category}</span>
                            <span><Calendar size={14} />{campaign.from} – {campaign.to}</span>
                            <span><MapPin size={14} />{campaign.country}</span>
                        </div>
                    </div>
                    <div>
                        <Button variant="outline" size="sm" style={{ marginRight: '8px' }}>Exportar</Button>
                        <Button size="sm">Ver entregables</Button>
                    </div>
                </div>

                {/* Tabs (Simple implementation) */}
                <div className={styles.tabsList}>
                    <button
                        className={styles.tabTrigger}
                        data-state={activeTab === 'details' ? 'active' : ''}
                        onClick={() => setActiveTab('details')}
                    >
                        Detalles
                    </button>
                    <button
                        className={styles.tabTrigger}
                        data-state={activeTab === 'metrics' ? 'active' : ''}
                        onClick={() => setActiveTab('metrics')}
                    >
                        Métricas
                    </button>
                    <button
                        className={styles.tabTrigger}
                        data-state={activeTab === 'creators' ? 'active' : ''}
                        onClick={() => setActiveTab('creators')}
                    >
                        Creadores
                    </button>
                </div>

                {activeTab === 'metrics' && (
                    <div className={styles.grid}>
                        <div className={styles.colLeft}>
                            <Card style={{ marginBottom: '16px' }}>
                                <CardHeader>
                                    <CardTitle>Totales de campaña</CardTitle>
                                    <CardDescription>Resumen consolidado</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className={styles.statGrid}>
                                        <Stat icon={<Eye size={14} />} label="Impresiones" value={totals.impressions} />
                                        <Stat icon={<Users size={14} />} label="Alcance" value={totals.reach} />
                                        <Stat icon={<Heart size={14} />} label="Likes" value={totals.likes} />
                                        <Stat icon={<MessageCircle size={14} />} label="Comentarios" value={totals.comments} />
                                        <Stat icon={<Bookmark size={14} />} label="Guardados" value={totals.saves} />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Rendimiento por creador</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {creators.map(c => (
                                        <div key={c.id} className={styles.creatorRow}>
                                            <div className={styles.creatorInfo}>
                                                <div className={styles.avatar}>
                                                    {c.handle.substring(1, 3).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{c.handle}</div>
                                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{c.posts} post • {c.er}% ER</div>
                                                </div>
                                            </div>
                                            <div>
                                                <Badge variant={c.status === 'Completada' ? 'secondary' : 'outline'}>{c.status}</Badge>
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>

                        <div className={styles.colRight}>
                            <div className={styles.summaryCard}>
                                <div style={{ fontWeight: 600, marginBottom: '12px' }}>Resumen</div>
                                <div className={styles.summaryRow}>
                                    <span>Valor por creador</span>
                                    <span>${campaign.valuePerCreator}</span>
                                </div>
                                <div className={styles.summaryRow}>
                                    <span>Plazas</span>
                                    <span>{campaign.slots}</span>
                                </div>
                                <div className={styles.summaryRow}>
                                    <span>Inversión Total</span>
                                    <span>${campaign.totalInvestment}</span>
                                </div>
                                <Button style={{ width: '100%', marginTop: '12px' }} size="sm">Gestionar campaña</Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignMetricsPage;
